if (!window.UgProductGallery) {
  class UgProductGallery {
    constructor(container) {
      this.container = container;
      this.main = container.querySelector("[data-gallery-main]");
      this.track = container.querySelector("[data-gallery-track]");
      this.mediaGallery = container.closest("media-gallery");
      this.mediaItems = Array.from(container.querySelectorAll(".ug-product-gallery__item"));
      this.imageItems = this.mediaItems.filter((item) => item.dataset.mediaType === "image");
      this.photoButtons = Array.from(container.querySelectorAll("[data-gallery-photo]"));
      this.thumbnailButtons = Array.from(container.querySelectorAll(".ug-product-gallery__thumb"));
      this.thumbsBar = container.querySelector("[data-gallery-thumbs]");
      this.photoControls = container.querySelector("[data-photo-controls]");
      this.mobilePagination = container.querySelector(".ug-product-gallery__mobile-pagination");
      this.timeline = container.querySelector("[data-video-timeline]");
      this.timeLabel = container.querySelector("[data-video-time]");
      this.muteButton = container.querySelector("[data-video-mute]");
      this.moreButton = container.querySelector("[data-gallery-more]");
      this.moreLabel = container.querySelector("[data-gallery-more-label]");
      this.previousMoreButton = container.querySelector("[data-gallery-more-previous]");
      this.previousMoreLabel = container.querySelector("[data-gallery-more-previous-label]");
      this.currentIndex = Math.max(0, this.mediaItems.findIndex((item) => item.classList.contains("is-active")));
      this.lastImageIndex = this.imageItems.length ? this.mediaItems.indexOf(this.imageItems[0]) : 0;
      this.thumbWindowStart = 0;
      this.isDragging = false;
      this.startX = 0;
      this.startY = 0;
      this.dragTranslate = 0;
      this.previousTranslate = 0;
      this.photoSwipe = null;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      if (!this.main || !this.track || !this.mediaItems.length) return;
      this.init();
    }

    get isDesktop() {
      return window.matchMedia("(min-width: 1024px)").matches;
    }

    init() {
      this.container.querySelectorAll("[data-gallery-nav]").forEach((button) => {
        button.addEventListener("click", () => {
          this.movePhoto(button.dataset.galleryNav === "next" ? 1 : -1);
        }, { signal: this.signal });
      });

      this.photoButtons.forEach((button) => {
        button.addEventListener("click", () => {
          const index = this.mediaItems.findIndex((item) => item.dataset.mediaId === button.dataset.galleryPhoto);
          if (index !== -1) this.moveToIndex(index);
        }, { signal: this.signal });
      });

      if (this.moreButton) {
        this.moreButton.addEventListener("click", () => {
          const nextStart = this.nextThumbnailStart();
          this.thumbWindowStart = nextStart;
          this.moveToIndex(this.mediaItems.indexOf(this.imageItems[nextStart]));
        }, { signal: this.signal });
      }

      if (this.previousMoreButton) {
        this.previousMoreButton.addEventListener("click", () => {
          const pageSize = this.thumbnailPageSize();
          const previousStart = Math.max(0, this.thumbWindowStart - pageSize);
          const previousEnd = Math.min(previousStart + pageSize - 1, this.imageItems.length - 1);
          this.thumbWindowStart = previousStart;
          this.moveToIndex(this.mediaItems.indexOf(this.imageItems[previousEnd]));
        }, { signal: this.signal });
      }

      this.container.querySelectorAll("[data-gallery-tab]").forEach((tab) => {
        tab.addEventListener("click", () => {
          if (tab.dataset.galleryTab === "photos") {
            this.moveToIndex(this.lastImageIndex);
            return;
          }
          const index = this.mediaItems.findIndex((item) => item.dataset.mediaId === tab.dataset.mediaId);
          if (index !== -1) this.moveToIndex(index);
        }, { signal: this.signal });
      });

      this.container.querySelectorAll("[data-video-toggle]").forEach((button) => {
        button.addEventListener("click", (event) => {
          event.stopPropagation();
          this.toggleVideo();
        }, { signal: this.signal });
      });

      this.muteButton?.addEventListener("click", () => this.toggleMute(), { signal: this.signal });
      this.timeline?.addEventListener("input", () => this.seekVideo(), { signal: this.signal });
      this.container.querySelector("[data-video-fullscreen]")?.addEventListener(
        "click",
        () => this.openFullscreen(),
        { signal: this.signal }
      );

      this.main.addEventListener("click", (event) => {
        if (!this.main.classList.contains("is-video")) return;
        if (event.target.closest("[data-video-controls], button, input")) return;
        this.toggleVideo();
      }, { signal: this.signal });

      this.main.addEventListener("pointerenter", (event) => {
        if (this.isDesktop && event.pointerType !== "touch") {
          this.setThumbnailsOpen(true);
        }
      }, { signal: this.signal });
      this.main.addEventListener("pointerleave", () => {
        if (this.isDesktop) this.setThumbnailsOpen(false);
      }, { signal: this.signal });
      this.main.addEventListener("keydown", (event) => {
        if (this.isDesktop && (event.key === "Tab" || event.key.startsWith("Arrow"))) {
          this.setThumbnailsOpen(true);
        }
      }, { signal: this.signal });
      this.main.addEventListener("focusout", () => {
        requestAnimationFrame(() => {
          if (!this.main.contains(document.activeElement) && !this.main.matches(":hover")) {
            this.setThumbnailsOpen(false);
          }
        });
      }, { signal: this.signal });

      this.track.addEventListener("touchstart", (event) => this.touchStart(event), {
        passive: true,
        signal: this.signal,
      });
      this.track.addEventListener("touchmove", (event) => this.touchMove(event), {
        passive: false,
        signal: this.signal,
      });
      this.track.addEventListener("touchend", () => this.touchEnd(), { signal: this.signal });

      window.addEventListener("resize", () => this.handleBreakpoint(), { signal: this.signal });
      this.initPagination();
      if (this.mediaGallery) {
        this.mediaGallery.setActiveMedia = (mediaId) => this.setActiveMedia(mediaId);
      }
      this.moveToIndex(this.currentIndex, false);
      this.handleBreakpoint();
    }

    destroy() {
      this.abortController.abort();
      this.pauseAllVideos();
    }

    movePhoto(direction) {
      if (!this.imageItems.length) return;
      const currentItem = this.mediaItems[this.currentIndex];
      let photoIndex = this.imageItems.indexOf(currentItem);
      if (photoIndex === -1) {
        photoIndex = this.imageItems.indexOf(this.mediaItems[this.lastImageIndex]);
      }
      const nextPhotoIndex = Math.max(0, Math.min(photoIndex + direction, this.imageItems.length - 1));
      this.moveToIndex(this.mediaItems.indexOf(this.imageItems[nextPhotoIndex]));
    }

    moveToIndex(index, animate = true) {
      const nextIndex = Math.max(0, Math.min(index, this.mediaItems.length - 1));
      const distance = Math.abs(nextIndex - this.currentIndex);
      this.currentIndex = nextIndex;
      const offset = nextIndex * -100;
      const duration = animate ? Math.min(360 + distance * 35, 560) : 0;

      this.track.style.transition = duration
        ? `transform ${duration}ms cubic-bezier(0.22, 1, 0.36, 1)`
        : "none";
      this.track.style.transform = `translate3d(${offset}%, 0, 0)`;
      this.mediaItems.forEach((item, itemIndex) => {
        item.classList.toggle("is-active", itemIndex === nextIndex);
      });
      if (!animate) {
        requestAnimationFrame(() => {
          this.track.style.transition = "";
        });
      }

      this.previousTranslate = offset;
      this.dragTranslate = offset;

      const activeItem = this.mediaItems[nextIndex];
      const isImage = activeItem.dataset.mediaType === "image";
      const isVideo = activeItem.dataset.mediaType === "video" || activeItem.dataset.mediaType === "external_video";
      const isExternalVideo = activeItem.dataset.mediaType === "external_video";
      this.main.classList.toggle("is-video", isVideo);
      this.main.classList.toggle("is-external-video", isExternalVideo);
      this.container.classList.toggle("is-video-active", isVideo);

      if (isImage) {
        this.lastImageIndex = nextIndex;
        this.pauseAllVideos();
      } else if (isVideo) {
        this.pauseAllVideos();
        this.prepareVideo(activeItem, true);
      } else {
        this.pauseAllVideos();
      }

      const activePhotoIndex = this.imageItems.indexOf(activeItem);
      if (activePhotoIndex !== -1) this.adjustThumbnailWindow(activePhotoIndex);
      this.updatePhotoControls(activeItem, activePhotoIndex);
      this.updateTabs(activeItem, isImage);
      this.updatePagination();
      this.updateNavigation(activePhotoIndex);
    }

    setThumbnailsOpen(isOpen) {
      this.main.classList.toggle("is-thumbnails-open", isOpen);
    }

    setActiveMedia(mediaId) {
      const targetIndex = this.mediaItems.findIndex((item) => item.dataset.mediaId === mediaId);
      if (targetIndex !== -1) this.moveToIndex(targetIndex);
    }

    updatePhotoControls(activeItem, activePhotoIndex) {
      this.photoButtons.forEach((button) => {
        const isActive = button.dataset.galleryPhoto === activeItem.dataset.mediaId;
        button.classList.toggle("is-active", isActive);
        if (button.classList.contains("ug-product-gallery__thumb")) {
          button.setAttribute("aria-current", String(isActive));
        }
      });
      this.renderThumbnailWindow(activePhotoIndex);
    }

    thumbnailPageSize() {
      return 5;
    }

    maxThumbnailStart(visibleCount = this.thumbnailPageSize()) {
      return Math.max(0, this.imageItems.length - visibleCount);
    }

    nextThumbnailStart() {
      const pageSize = this.thumbnailPageSize();
      return Math.min(this.thumbWindowStart + pageSize, this.maxThumbnailStart(pageSize));
    }

    adjustThumbnailWindow(activePhotoIndex) {
      const pageSize = this.thumbnailPageSize();
      if (this.imageItems.length <= this.thumbnailPageSize()) {
        this.thumbWindowStart = 0;
        return;
      }
      if (activePhotoIndex < this.thumbWindowStart) {
        this.thumbWindowStart = Math.floor(activePhotoIndex / pageSize) * pageSize;
      } else if (activePhotoIndex >= this.thumbWindowStart + pageSize) {
        this.thumbWindowStart = Math.min(
          Math.floor(activePhotoIndex / pageSize) * pageSize,
          this.maxThumbnailStart(pageSize)
        );
      }
    }

    renderThumbnailWindow(activePhotoIndex) {
      const hasOverflow = this.imageItems.length > this.thumbnailPageSize();
      const visibleCount = hasOverflow ? this.thumbnailPageSize() : this.imageItems.length;
      const maxStart = this.maxThumbnailStart(visibleCount);
      this.thumbWindowStart = Math.min(this.thumbWindowStart, maxStart);
      const windowEnd = this.thumbWindowStart + visibleCount;
      const previousCount = this.thumbWindowStart;
      const remaining = Math.max(0, this.imageItems.length - windowEnd);

      if (this.thumbsBar) {
        const safeVisibleCount = Math.max(visibleCount, 1);
        const thumbnailSize = 40;
        const thumbnailGap = 16;
        const thumbnailStep = thumbnailSize + thumbnailGap;
        const viewportWidth = safeVisibleCount * thumbnailSize + (safeVisibleCount - 1) * thumbnailGap;
        this.thumbsBar.style.setProperty(
          "--ug-thumb-viewport-collapsed",
          `${viewportWidth}px`
        );
        this.thumbsBar.style.setProperty(
          "--ug-thumb-viewport-expanded",
          `${viewportWidth}px`
        );
        this.thumbsBar.style.setProperty(
          "--ug-thumb-offset-collapsed",
          `${this.thumbWindowStart * -thumbnailStep}px`
        );
        this.thumbsBar.style.setProperty(
          "--ug-thumb-offset-expanded",
          `${this.thumbWindowStart * -thumbnailStep}px`
        );
        const overflowButtonCount = Number(hasOverflow && previousCount > 0)
          + Number(hasOverflow && remaining > 0);
        const controlsWidth = viewportWidth + overflowButtonCount * thumbnailStep;
        this.photoControls?.style.setProperty("--ug-photo-controls-width", `${controlsWidth}px`);
      }

      if (this.moreButton) {
        this.moreButton.style.display = hasOverflow && remaining > 0 ? "block" : "none";
        this.moreButton.disabled = remaining <= 0;
        const nextPreview = this.thumbnailButtons[windowEnd]?.querySelector("img");
        const nextPreviewUrl = nextPreview?.currentSrc || nextPreview?.src || "";
        this.moreButton.style.setProperty(
          "--ug-more-image",
          nextPreviewUrl ? `url(${JSON.stringify(nextPreviewUrl)})` : "none"
        );
        if (this.moreLabel) this.moreLabel.textContent = remaining > 0 ? `${remaining}+` : "";
        this.moreButton.setAttribute("aria-label", remaining > 0 ? `Show ${remaining} more photos` : "No more photos");
      }

      if (this.previousMoreButton) {
        this.previousMoreButton.style.display = hasOverflow && previousCount > 0 ? "block" : "none";
        this.previousMoreButton.disabled = previousCount <= 0;
        const previousPreview = this.thumbnailButtons[this.thumbWindowStart - 1]?.querySelector("img");
        const previousPreviewUrl = previousPreview?.currentSrc || previousPreview?.src || "";
        this.previousMoreButton.style.setProperty(
          "--ug-more-image",
          previousPreviewUrl ? `url(${JSON.stringify(previousPreviewUrl)})` : "none"
        );
        if (this.previousMoreLabel) {
          this.previousMoreLabel.textContent = previousCount > 0 ? `${previousCount}+` : "";
        }
        this.previousMoreButton.setAttribute(
          "aria-label",
          previousCount > 0 ? `Show ${previousCount} previous photos` : "No previous photos"
        );
      }

      if (activePhotoIndex !== -1 && activePhotoIndex >= windowEnd && hasOverflow) {
        this.thumbWindowStart = this.maxThumbnailStart(visibleCount);
        this.renderThumbnailWindow(activePhotoIndex);
      }
    }

    updateTabs(activeItem, isImage) {
      this.container.querySelectorAll("[data-gallery-tab]").forEach((tab) => {
        const isActive = isImage
          ? tab.dataset.galleryTab === "photos"
          : tab.dataset.mediaId === activeItem.dataset.mediaId;
        tab.classList.toggle("is-active", isActive);
        tab.setAttribute("aria-selected", String(isActive));
      });
    }

    updateNavigation(activePhotoIndex) {
      const previous = this.container.querySelector('[data-gallery-nav="previous"]');
      const next = this.container.querySelector('[data-gallery-nav="next"]');
      if (previous) previous.disabled = activePhotoIndex <= 0;
      if (next) next.disabled = activePhotoIndex === -1 || activePhotoIndex >= this.imageItems.length - 1;
    }

    initPagination() {
      if (!this.mobilePagination) return;
      this.mobilePagination.replaceChildren();
      this.mediaItems.forEach((_, index) => {
        const dot = document.createElement("button");
        dot.type = "button";
        dot.className = "ug-product-gallery__mobile-dot";
        dot.setAttribute("aria-label", `Show media ${index + 1}`);
        dot.addEventListener("click", () => this.moveToIndex(index), { signal: this.signal });
        this.mobilePagination.appendChild(dot);
      });
      this.mobileDots = Array.from(this.mobilePagination.children);
    }

    updatePagination() {
      this.mobileDots?.forEach((dot, index) => {
        dot.classList.toggle("is-active", index === this.currentIndex);
      });
    }

    touchStart(event) {
      if (this.isDesktop || !event.touches.length) return;
      this.isDragging = true;
      this.startX = event.touches[0].clientX;
      this.startY = event.touches[0].clientY;
      this.dragTranslate = this.previousTranslate;
    }

    touchMove(event) {
      if (!this.isDragging || !event.touches.length) return;
      const diffX = event.touches[0].clientX - this.startX;
      const diffY = event.touches[0].clientY - this.startY;
      if (Math.abs(diffX) <= Math.abs(diffY) || Math.abs(diffX) < 5) return;
      event.preventDefault();
      this.dragTranslate = this.previousTranslate + (diffX / this.track.offsetWidth) * 100;
      this.track.style.transition = "none";
      this.track.style.transform = `translate3d(${this.dragTranslate}%, 0, 0)`;
    }

    touchEnd() {
      if (!this.isDragging) return;
      this.isDragging = false;
      const movedBy = this.dragTranslate - this.previousTranslate;
      if (Math.abs(movedBy) > 20) {
        this.moveToIndex(this.currentIndex + (movedBy < 0 ? 1 : -1));
      } else {
        this.moveToIndex(this.currentIndex);
      }
    }

    prepareVideo(item, shouldPlay) {
      if (item.dataset.mediaType === "external_video") {
        const iframe = item.querySelector("iframe");
        if (iframe) {
          this.controlExternalVideo(iframe, shouldPlay ? "play" : "pause");
          return;
        }
        item.querySelector("deferred-media .deferred-media__poster")?.click();
        return;
      }
      const loadedVideo = item.querySelector("video");
      if (loadedVideo) {
        this.configureVideo(loadedVideo, shouldPlay);
        return;
      }

      const deferredMedia = item.querySelector("deferred-media");
      const poster = deferredMedia?.querySelector(".deferred-media__poster");
      if (!deferredMedia || !poster) return;

      const observer = new MutationObserver(() => {
        const video = item.querySelector("video");
        if (!video) return;
        observer.disconnect();
        this.configureVideo(video, shouldPlay);
      });
      observer.observe(deferredMedia, { childList: true, subtree: true });
      poster.click();
    }

    configureVideo(video, shouldPlay) {
      video.controls = false;
      video.muted = true;
      video.playsInline = true;

      if (!video.dataset.ugGalleryReady) {
        video.dataset.ugGalleryReady = "true";
        ["loadedmetadata", "durationchange", "timeupdate"].forEach((eventName) => {
          video.addEventListener(eventName, () => this.updateVideoProgress(video), { signal: this.signal });
        });
        video.addEventListener("play", () => {
          this.updateVideoPlaybackState(video, true);
        }, { signal: this.signal });
        video.addEventListener("pause", () => {
          this.updateVideoPlaybackState(video, false);
        }, { signal: this.signal });
        video.addEventListener("ended", () => this.updateVideoPlaybackState(video, false), { signal: this.signal });
      }

      if (shouldPlay) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
      this.muteButton?.classList.toggle("is-unmuted", !video.muted);
      this.updateVideoProgress(video);
    }

    updateVideoPlaybackState(video, isPlaying) {
      if (this.getCurrentVideo() !== video) return;
      this.main.classList.toggle("is-playing", isPlaying);
      this.container.querySelectorAll("[data-video-toggle]").forEach((button) => {
        button.setAttribute("aria-label", isPlaying ? "Pause video" : "Play video");
      });
    }

    getCurrentVideo() {
      return this.mediaItems[this.currentIndex]?.querySelector("video") || null;
    }

    toggleVideo() {
      const item = this.mediaItems[this.currentIndex];
      let video = this.getCurrentVideo();
      if (!video) {
        this.prepareVideo(item, true);
        return;
      }
      if (video.paused || video.ended) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    }

    pauseAllVideos() {
      this.mediaItems.forEach((item) => {
        item.querySelector("video")?.pause();
        item.querySelectorAll("iframe").forEach((iframe) => {
          this.controlExternalVideo(iframe, "pause");
        });
      });
      this.main.classList.remove("is-playing");
    }

    controlExternalVideo(iframe, action) {
      const source = iframe.src || "";
      if (source.includes("youtube.com") || source.includes("youtu.be")) {
        iframe.contentWindow?.postMessage(JSON.stringify({
          event: "command",
          func: action === "play" ? "playVideo" : "pauseVideo",
          args: [],
        }), "*");
      } else if (source.includes("vimeo.com")) {
        iframe.contentWindow?.postMessage({ method: action }, "*");
      }
    }

    toggleMute() {
      const video = this.getCurrentVideo();
      if (!video) return;
      video.muted = !video.muted;
      this.muteButton?.classList.toggle("is-unmuted", !video.muted);
      this.muteButton?.setAttribute("aria-label", video.muted ? "Unmute video" : "Mute video");
    }

    seekVideo() {
      const video = this.getCurrentVideo();
      if (!video || !Number.isFinite(video.duration)) return;
      video.currentTime = (Number(this.timeline.value) / 100) * video.duration;
    }

    updateVideoProgress(video) {
      if (this.getCurrentVideo() !== video) return;
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      const progress = duration ? (video.currentTime / duration) * 100 : 0;
      if (this.timeline) {
        this.timeline.value = String(progress);
        this.timeline.style.setProperty("--ug-video-progress", `${progress}%`);
      }
      if (this.timeLabel) {
        this.timeLabel.textContent = `${this.formatTime(video.currentTime)} / ${this.formatTime(duration)}`;
      }
    }

    formatTime(value) {
      const seconds = Math.max(0, Math.floor(Number(value) || 0));
      const minutes = Math.floor(seconds / 60);
      return `${String(minutes).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
    }

    openFullscreen() {
      const video = this.getCurrentVideo();
      if (!video) return;
      if (video.requestFullscreen) {
        video.requestFullscreen();
      } else if (video.webkitEnterFullscreen) {
        video.webkitEnterFullscreen();
      }
    }

    handleBreakpoint() {
      if (!this.isDesktop) this.setThumbnailsOpen(false);
      this.mediaItems.forEach((item) => {
        const video = item.querySelector("video");
        if (video) video.controls = false;
      });
    }

  }

  window.UgProductGallery = UgProductGallery;

  const initializeProductGalleries = (root = document) => {
    const galleries = Array.from(root.querySelectorAll?.("[data-product-gallery]") || []);
    galleries.forEach((gallery) => {
      if (gallery.ugProductGallery) return;
      gallery.ugProductGallery = new UgProductGallery(gallery);
    });
    const instances = Array.from(document.querySelectorAll("[data-product-gallery]"))
      .map((gallery) => gallery.ugProductGallery)
      .filter(Boolean);
    window.productGalleryInstances = instances;
    window.productGalleryInstance = {
      setActiveMedia(mediaId) {
        instances.find((instance) => instance.mediaItems.some((item) => item.dataset.mediaId === mediaId))
          ?.setActiveMedia(mediaId);
      },
    };
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeProductGalleries(), { once: true });
  } else {
    initializeProductGalleries();
  }

  document.addEventListener("shopify:section:load", (event) => initializeProductGalleries(event.target));
}
